import { Attachment, GeneralRequestResponse } from "./save-request.model";
import { GeneralRequestObj } from "./general-request.model";
import { ClassificatorCode } from "../../../services/fetch-classificators.service";
import { Person } from "../../video-meeting.model";
import { Register } from "../../session-context.model";
import { addDays, formatISO, startOfDay } from "date-fns";

export enum FilterElementType {
  FILTER = "filter",
  FIELD = "field",
  AND = "and",
  OR = "or",
  SORT = "sort",
}

export enum SortOrder {
  ASC = "asc",
  DESC = "desc",
}

export class RequestPayload implements GeneralRequestObj {
  readonly mode: string = "SAVE";
  items: any;

  constructor(mode: string, items: any) {
    this.items = items;
    this.mode = mode;
  }
}

export class FetchPayload {
  readonly mode: string = "FETCH";
  filter: Filter;
  pagination: Pagination;
  application: string;

  constructor(
    elementList: FilterElement[],
    pagination: Pagination,
    includeRelation: string[],
  ) {
    this.filter = {
      type: FilterElementType.FILTER,
      element: elementList,
      includeRelation,
    };
    this.pagination = pagination;
    this.application = "PATIENT";
  }
}

export interface FilterElement {
  type: FilterElementType;
  name?: string;
  order?: string;
  value?: string[] | number[] | boolean[];
  low?: string;
  high?: string;
  includeRelation?: string[];
  excludeRelation?: string[];
  include?: string[];
  exclude?: string[];
  isNull?: boolean;
  partial?: boolean;
  nullsLast?: boolean;
}

export interface Filter extends FilterElement {
  type: FilterElementType;
  element: FilterElement[];
  includeRelation?: string[];
  name?: string;
  excludeRelation?: string[];
  include?: string[];
  exclude?: string[];
}

export interface Pagination {
  limit: number;
  offset: number;
  count?: number;
  totalCount?: number;
  useCount?: boolean;
}

interface GeneralField extends FilterElement {
  type: FilterElementType;
  name?: string;
  order?: string;
  value?: string[];
  low?: string;
  high?: string;
  includeRelation?: string[];
  excludeRelation?: string[];
  include?: string[];
  exclude?: string[];
  isNull?: boolean;
  partial?: boolean;
  nullsLast?: boolean;
}

class FilterField implements GeneralField {
  type = FilterElementType.FIELD;
  name: string;
  value: string[];
  partial: boolean;

  constructor(name: string, value: string[], partial?: boolean) {
    this.name = name;
    this.value = value;
    this.partial = partial ? partial : null;
  }
}

class And implements Filter {
  type = FilterElementType.AND;
  element: FilterElement[];

  constructor(element: FilterElement[]) {
    this.element = element;
  }
}

class Or implements Filter {
  type = FilterElementType.OR;
  element: FilterElement[];

  constructor(element: FilterElement[]) {
    this.element = element;
  }
}

class Sort implements GeneralField {
  type = FilterElementType.SORT;
  name: string;
  order: string;

  constructor(name: string, isDescending: boolean) {
    this.name = name;
    this.order = isDescending ? "desc" : "asc";
  }
}

export class FetchMessagesPayload extends FetchPayload {
  constructor(params: FetchMessagesPayloadParams, directionCodeIndex: number, idField: string) {
    const id = JSON.parse(sessionStorage.getItem("sessionContext"))
      ? JSON.parse(sessionStorage.getItem("sessionContext")).principal.id
      : null;

    const directionCode = params.directionCodeId[directionCodeIndex];

    const elementList: FilterElement[] = [
      new FilterField("Message.directionCodeId", [directionCode.toString()], false),
      new And([new FilterField(idField, [id.toString()], false)]),
      new Sort("Message.insertTime", true),
    ];

    const offs = params.offset ? params.offset : 0;
    const pagination: Pagination = {
      limit: params.paginationLimit,
      offset: offs,
    };
    super(elementList, pagination, null);
  }
}

export class FetchMessageThreadPayload extends FetchPayload {
  constructor(id: string) {
    const elementList: FilterElement[] = [
      {
        type: FilterElementType.SORT,
        name: "Message.insertTime",
        order: "desc",
      },
      {
        type: FilterElementType.FIELD,
        name: "Message.id",
        value: [id],
      },
    ];

    super(elementList, null, [
      "masterMessage",
      "attachmentList",
      "sentByPerson",
      "sentToPerson",
      "typeCode",
    ]);
  }
}

export interface FetchMessagesPayloadParams {
  paginationLimit: number;
  directionCodeId: string[];
  offset?: number;
}

export class FetchMeetingsPayload extends FetchPayload {
  constructor() {
    const now = new Date();
    const todayStart = startOfDay(now);
    const weekAhead = addDays(todayStart, 7);

    const elementList: FilterElement[] = [
      {
        type: FilterElementType.FIELD,
        name: "Meeting.meetingTime",
        low: formatISO(todayStart),
        high: formatISO(weekAhead),
      },
      {
        type: FilterElementType.SORT,
        name: "Meeting.meetingTime",
        order: "asc",
      },
    ];

    const pagination: Pagination = {
      limit: 20,
      offset: 0,
    };
    super(elementList, pagination, [
      "person",
      "patient",
      "doctor",
      "type",
      "status",
    ]);
  }
}


export class FetchAttachmentsPayload extends FetchPayload {
  constructor(messageId: string) {
    const elementList: FilterElement[] = [
      {
        type: FilterElementType.FIELD,
        name: "Attachment.messageId",
        value: [messageId],
      },
    ];
    const pagination: Pagination = {
      limit: 5,
      offset: 0,
    };
    super(elementList, pagination, null);
  }
}

export class FetchDiseasesPayload extends FetchPayload {
  constructor(
    observationClassificatorList: string[],
    paginationLimit: number,
    includeRelation: string[],
  ) {
    const elementList: FilterElement[] = [
      {
        type: FilterElementType.FIELD,
        name: "Observation.classificatorCodeId",
        value: observationClassificatorList,
      },
      {
        type: FilterElementType.SORT,
        name: "Observation.id",
        order: SortOrder.DESC,
      },
    ];
    const pagination: Pagination = {
      limit: paginationLimit,
      offset: 0,
    };
    super(elementList, pagination, includeRelation);
  }
}

export class FetchObservationsPayload extends FetchPayload {
  constructor(
    offset: number,
    includeRelation: string[],
    personId: number,
    classificators: number[],
  ) {
    let classificatorsArray = [];
    classificators.forEach((classificator, index) => {
      if (index == 0) {
        classificatorsArray.push(
          new FilterField(
            "Observation.classificatorCodeId",
            [classificator.toString()],
            false,
          ),
        );
        classificatorsArray.push(new Sort("Observation.id", true));
      } else {
        classificatorsArray.push(
          new Or([
            new FilterField(
              "Observation.classificatorCodeId",
              [classificator.toString()],
              false,
            ),
          ]),
        );
      }
    });
    const elementList: FilterElement[] = [
      new FilterField("Observation.personId", [personId.toString()], false),
      new And(classificatorsArray),
    ];
    const pagination: Pagination = {
      limit: 100,
      offset: offset,
    };
    super(elementList, pagination, includeRelation);
  }
}

export class FetchHealthPlanPayload extends FetchPayload {
  constructor(
    paginationLimit: number,
    includeRelation: string[],
    personId: number,
  ) {
    const elementList: FilterElement[] = [
      new FilterField("HealthPlan.personId", [personId.toString()], false),
      new Sort("HealthPlan.id", true),
    ];
    const pagination: Pagination = {
      limit: 100,
      offset: 0,
    };
    super(elementList, pagination, includeRelation);
  }
}

export class MedicationRequestPayload extends FetchPayload {
  constructor(
    offset: number,
    includeRelation: string[],
    prescribedMedications: number[],
  ) {
    let fetchIds: string[] = prescribedMedications.map((derivedFrom) =>
      derivedFrom.toString(),
    );
    const elementList: FilterElement[] = [
      new FilterField("MedicationRequest.id", fetchIds, false),
    ];
    const pagination: Pagination = {
      limit: 100,
      offset: offset,
    };
    super(elementList, pagination, includeRelation);
  }
}

export class MedicationPayload extends FetchPayload {
  constructor(
    offset: number,
    includeRelation: string[],
    regularMedications: number[],
  ) {
    let fetchIds: string[] = regularMedications.map((derivedFrom) =>
      derivedFrom.toString(),
    );
    const elementList: FilterElement[] = [
      new FilterField("Medication.id", fetchIds, false),
    ];
    const pagination: Pagination = {
      limit: 100,
      offset: offset,
    };
    super(elementList, pagination, includeRelation);
  }
}

export class FetchAddressDetailsPayload extends FetchPayload {
  constructor(institutionId: number) {
    const elementList: FilterElement[] = [
      {
        type: FilterElementType.FIELD,
        name: "Address.institutionId",
        value: [institutionId.toString()],
      },
    ];
    const pagination: Pagination = {
      limit: 30,
      offset: 0,
    };
    const includeRelation: string[] = ["institution"];

    super(elementList, pagination, includeRelation);
  }
}

export class FetchContactDetailsPayload extends FetchPayload {
  constructor(institutionId: number) {
    const elementList: FilterElement[] = [
      {
        type: FilterElementType.FIELD,
        name: "Contact.institutionId",
        value: [institutionId.toString()],
      },
      {
        type: FilterElementType.FIELD,
        name: "Contact.personId",
        isNull: true,
      },
    ];
    const pagination: Pagination = {
      limit: 30,
      offset: 0,
    };
    const includeRelation: string[] = ["institution"];

    super(elementList, pagination, includeRelation);
  }
}

export class CheckMedicationsPayload {
  personalCode: string;
  from: string;
  until: string;
  application;

  constructor(personalCode, from?: Date, until?: Date) {
    this.personalCode = personalCode;
    this.application = "PATIENT";

    if (from && until && from.getTime() > until.getTime()) {
      const temp = from;
      from = until;
      until = temp;
    }

    if (from) {
      this.from = from.toISOString();
    }
    if (until) {
      this.until = until.toISOString();
    }
  }
}

export class FetchInstitutionNamesPayload extends FetchPayload {
  constructor(registerIds: number[]) {
    const elementList: FilterElement[] = [
      {
        type: FilterElementType.FIELD,
        name: "RegisterInstitution.registerId",
        value: registerIds.map(id => id.toString()),
      },
    ];
    const pagination: Pagination = {
      limit: 30,
      offset: 0,
    };
    const includeRelation: string[] = ["institution"];

    super(elementList, pagination, includeRelation);
  }
}

export class MotivationalQuestionnairePayload extends FetchPayload {
  constructor(
    offset: number,
    includeRelation: string[],
    personId: number,
    questionnaireClassificatorCode: number,
  ) {
    const elementList: FilterElement[] = [
      new FilterField(
        "Observation.classificatorCodeId",
        [questionnaireClassificatorCode.toString()],
        false,
      ),
      new Sort("Observation.id", true),
    ];
    const pagination: Pagination = {
      limit: 1,
      offset: offset,
    };
    super(elementList, pagination, includeRelation);
  }
}

export class HealthPlanRelationHelperPayload extends FetchPayload {
  constructor(
    offset: number,
    includeRelation: string[],
    id: number[],
    personId: number,
    tableName: string,
  ) {
    let filtersArray = [];
    tableName = tableName.charAt(0).toUpperCase() + tableName.slice(1);

    id.forEach((id, index) => {
      if (index == 0) {
        filtersArray.push(new FilterField(tableName, [id.toString()], false));
        filtersArray.push(new Sort(tableName, true));
      } else {
        filtersArray.push(
          new Or([new FilterField(tableName, [id.toString()], false)]),
        );
      }
    });

    const elementList: FilterElement[] = filtersArray;
    const pagination: Pagination = {
      limit: 100,
      offset: offset,
    };
    super(elementList, pagination, includeRelation);
  }
}

export class FetchDevicesPayload extends FetchPayload {
  constructor(
    paginationLimit: number,
    includeRelation: string[],
    personId: number,
  ) {
    const elementList: FilterElement[] = [
      {
        type: FilterElementType.FIELD,
        name: "Device",
      },
      new FilterField("Device.personId", [personId.toString()], false),
    ];
    const pagination: Pagination = {
      limit: 100,
      offset: 0,
    };
    super(elementList, pagination, includeRelation);
  }
}

export interface FetchMessagesResponse extends GeneralRequestResponse {
  items: FetchMessagesResponseItem[];
}

export interface FetchMessagesResponseItem {
  aktRequirement?: boolean;
  archived: boolean;
  attachmentsExist: boolean;
  attachmentList?: Attachment[];
  content: string;
  directionCode?: ClassificatorCode;
  directionCodeId: number;
  id: number;
  insertTime: string;
  levelCode?: ClassificatorCode;
  levelCodeId: number;
  masterMessage?: FetchMessagesResponseItem;
  masterMessageId?: number;
  person?: Person;
  personId: number;
  register: Register;
  registerId: number;
  representerPerson?: Person;
  representerPersonId: number;
  sentByPerson?: Person;
  sentByPersonId: number;
  senToPerson?: Person;
  sentToPersonId: number;
  statusCode?: ClassificatorCode;
  statusCodeId: number;
  subject: string;
  threadTop: boolean;
  type: string;
  typeCode?: ClassificatorCode;
  typeCodeId: number;
  updateTime: string;
  requiredTestId: number;
  crossCommunicationStatus: boolean;
  messageSendTime?: string;
  orderedServiceId?: number;
  prescriptionStatusId?: number;
}
